import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  PageHeader, DataTable, Tag, Dialog, Button, SimpleTable, StatGrid, StatTile, useCkToast,
} from '@NP-Webber/crm-kit/dist/next/index.js';
import api from '../../services/api.js';
import { formatHebrewDateTime } from '../../utils/hebrewDate';
import { apiError } from '../../next/ui';

/**
 * הזמנות שאלונים — גבאים, new design. Same endpoints as GabbaiOrdersSummary: the summary by exam + location,
 * per-row breakdown of the kolels (gabbaim) that ordered, and the three exports (orders Excel, material Excel/PDF).
 */
const XLSX = 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet';

function downloadBlob(data, filename, mime) {
  const url = URL.createObjectURL(new Blob([data], { type: mime }));
  const link = document.createElement('a');
  link.href = url;
  link.download = filename;
  link.click();
  URL.revokeObjectURL(url);
}

const kolelLabel = (g) => (g.kolelName ? `${g.kolelName}${g.kolelCode != null && g.kolelCode !== '' ? ` (${g.kolelCode})` : ''}` : '—');

export default function GabbaiOrdersSummaryNext() {
  const toast = useCkToast();
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [exporting, setExporting] = useState('');
  const [openRow, setOpenRow] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const res = await api.get('/gabbai-orders/summary');
      setData((res.data.data || []).map((r) => ({ ...r, _key: `${r.examId}::${r.location}` })));
    } catch (e) {
      setError(apiError(e, 'שגיאה בטעינת הזמנות גבאים'));
    } finally {
      setLoading(false);
    }
  }, []);
  useEffect(() => { load(); }, [load]);

  const day = () => new Date().toISOString().slice(0, 10);
  const exportOrders = async () => {
    setExporting('orders');
    try {
      const response = await api.get('/gabbai-orders/export-excel', { responseType: 'blob' });
      downloadBlob(response.data, `הזמנות_גבאים_${day()}.xlsx`, XLSX);
    } catch {
      toast.error('שגיאה בייצוא הזמנות');
    } finally {
      setExporting('');
    }
  };
  const exportMaterial = async (format) => {
    setExporting(format);
    try {
      const response = await api.get(format === 'pdf' ? '/gabbai-orders/export-material-pdf' : '/gabbai-orders/export-material-excel', { responseType: 'blob' });
      if (format === 'pdf') downloadBlob(response.data, `סיכום_חומר_הזמנות_${day()}.pdf`, 'application/pdf');
      else downloadBlob(response.data, `סיכום_חומר_הזמנות_${day()}.xlsx`, XLSX);
    } catch {
      toast.error('שגיאה בייצוא סיכום חומר');
    } finally {
      setExporting('');
    }
  };

  const totals = useMemo(() => ({
    quantity: data.reduce((s, r) => s + (Number(r.totalQuantity) || 0), 0),
    exams: new Set(data.map((r) => String(r.examId))).size,
    kolels: new Set(data.flatMap((r) => (r.gabbaim || []).map((g) => g.kolelCode ?? g.kolelName ?? g.orderId))).size,
  }), [data]);

  const disabled = !!exporting || loading || !data.length;
  const columns = [
    { key: 'examCode', label: 'קוד מבחן', ltr: true, getValue: (r) => r.examCode || '—' },
    { key: 'examName', label: 'מבחן', render: (r) => <b>{r.examName || '—'}</b> },
    { key: 'location', label: 'עיר מיקום', type: 'enum', getValue: (r) => r.location || '—' },
    { key: 'totalQuantity', label: 'סה״כ כמות', type: 'number', render: (r) => <Tag tone="primary">{r.totalQuantity}</Tag> },
    { key: 'gabbaimCount', label: 'גבאים', type: 'number', getValue: (r) => r.gabbaim?.length || 0 },
  ];

  return (
    <>
      <PageHeader
        title="הזמנות שאלונים — גבאים"
        sub="סה״כ כמויות לפי מבחן ועיר מיקום. כל כולל (גבאי) מעדכן רק את השורה שלו; הסכום מצטבר מכל הכוללים."
        menus={[{
          key: 'export',
          label: 'ייצוא',
          icon: 'download',
          items: [
            { key: 'orders', label: exporting === 'orders' ? 'מייצא…' : 'ייצוא הזמנות Excel', icon: 'download', onClick: exportOrders, disabled },
            { key: 'excel', label: exporting === 'excel' ? 'מייצא…' : 'סיכום חומר Excel', icon: 'download', onClick: () => exportMaterial('excel'), disabled },
            { key: 'pdf', label: exporting === 'pdf' ? 'מייצא…' : 'סיכום חומר PDF', icon: 'file', onClick: () => exportMaterial('pdf'), disabled },
          ],
        }]}
        more={[{ key: 'refresh', label: 'רענון', icon: 'refresh', onClick: load }]}
      />

      <StatGrid>
        <StatTile label="סה״כ שאלונים שהוזמנו" value={totals.quantity.toLocaleString('he-IL')} icon="file" tone="primary" loading={loading && !data.length} />
        <StatTile label="מבחנים" value={totals.exams} icon="task" loading={loading && !data.length} />
        <StatTile label="כוללים שהזמינו" value={totals.kolels} icon="users" loading={loading && !data.length} />
      </StatGrid>

      <DataTable
        mode="client"
        rows={data}
        rowKey="_key"
        columns={columns}
        loading={loading}
        error={error}
        onRetry={load}
        onRefresh={load}
        storageKey="bchinot-gabbai-orders"
        selectable={false}
        exportFilename="הזמנות_גבאים"
        exportNote="ייצוא הזמנות — קוד מבחן, שם וכמות מצטברת. ייצוא סיכום חומר (מתפריט ייצוא) — ארבעת חלקי השו״ע וטווחי הסימנים."
        footerTotals={{ totalQuantity: <b>{totals.quantity.toLocaleString('he-IL')}</b> }}
        emptyTitle="אין הזמנות גבאים עדיין"
        onRowClick={setOpenRow}
        rowActions={(r) => [{ key: 'open', icon: 'eye', label: 'פירוט גבאים', onClick: () => setOpenRow(r) }]}
      />

      <Dialog
        open={!!openRow}
        onClose={() => setOpenRow(null)}
        title={openRow ? `${openRow.examName || '—'}${openRow.location ? ` · ${openRow.location}` : ''}` : ''}
        icon="users"
        size="md"
        footer={<Button variant="ghost" onClick={() => setOpenRow(null)}>סגירה</Button>}
      >
        {openRow ? (
          <SimpleTable
            rowKey="orderId"
            rows={openRow.gabbaim || []}
            columns={[
              { key: 'kolel', label: 'כולל', render: kolelLabel },
              { key: 'gabbaiName', label: 'גבאי', render: (g) => g.gabbaiName || '—' },
              { key: 'phone', label: 'טלפון', render: (g) => <span dir="ltr">{g.phone || '—'}</span> },
              { key: 'quantity', label: 'כמות', render: (g) => <b>{g.quantity}</b> },
              { key: 'updatedAt', label: 'עודכן', render: (g) => (g.updatedAt ? formatHebrewDateTime(g.updatedAt) : '—') },
            ]}
          />
        ) : null}
      </Dialog>
    </>
  );
}
